import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { getCustomFieldService } from "@/services/custom-field.service";

import type {
  CustomFieldAnswerDto,
  CustomFieldsData,
  CustomFieldsValidationResult,
  SyncCustomFieldAnswerDto,
  SyncCustomFieldAnswersResult,
} from "@smeetz/custom-field-common";

/**
 * Pinia store for managing custom fields in orders.
 */
export const useCustomFieldStore = defineStore("custom-field", () => {
  const customFields = ref<CustomFieldsData[]>([]);
  const validationResult = ref<CustomFieldsValidationResult | null>(null);

  const getCustomFields = computed(() => customFields.value);

  /**
   * Get custom fields from the local DB
   *
   * @param {string} orderId - The ID of the order to fetch custom fields.
   * @returns {Promise<CustomFieldsData[]>}
   */
  async function fetchOrderCustomFields(
    orderId: string
  ): Promise<CustomFieldsData[]> {
    const service = getCustomFieldService();

    try {
      const response = await service.fetchOrderCustomFields(orderId);

      customFields.value = response;

      return response;
    } catch (error) {
      const resolvedError =
        error instanceof Error
          ? error
          : new Error("Failed to fetch order custom fields");
      throw resolvedError;
    }
  }

  /**
   * Validate custom fields for an order.
   *
   * @param {string} orderId - The ID of the order to validate custom fields.
   * @param {CustomFieldAnswerDto[]} answers - The answers provided for the custom fields.
   * @returns {Promise<CustomFieldsValidationResult>}
   */
  async function validateOrderCustomFields(
    orderId: string,
    answers: CustomFieldAnswerDto[]
  ): Promise<CustomFieldsValidationResult> {
    const service = getCustomFieldService();

    try {
      const result = await service.validateOrderCustomFields(orderId, answers);

      return result;
    } catch (error) {
      const resolvedError =
        error instanceof Error
          ? error
          : new Error("Failed to validate order custom fields");
      throw resolvedError;
    }
  }

  /**
   * Sync custom field answers with the server.
   *
   * @param {SyncCustomFieldAnswerDto[]} answers - The answers to sync.
   * @param {FormData | Record<string, File>} [files] - Optional files to include in the sync.
   * @returns {Promise<SyncCustomFieldAnswersResult>}
   */
  async function syncAnswers(
    answers: SyncCustomFieldAnswerDto[],
    files?: FormData | Record<string, File>
  ): Promise<SyncCustomFieldAnswersResult> {
    const service = getCustomFieldService();

    try {
      const result = await service.syncAnswers(answers, files);

      return result;
    } catch (error) {
      const resolvedError =
        error instanceof Error
          ? error
          : new Error("Failed to sync custom field answers");
      throw resolvedError;
    }
  }

  function resetCustomFields(): void {
    customFields.value = [];
    validationResult.value = null;
  }

  return {
    getCustomFields,
    fetchOrderCustomFields,
    validateOrderCustomFields,
    syncAnswers,
    resetCustomFields,
  };
});
